import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  datePresets,
  getDateRange,
  normalizeDayRange,
} from "@/pages/workshop/utils/dateRange";

const VALID_PRESETS = new Set(datePresets.map((p) => p.value));

function parseISODate(v: string | null): Date | null {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d;
}

/** Logger centralizado. Trackeable y fácil de mockear en tests. */
function warn(reason: string, detail: Record<string, unknown>) {
  // eslint-disable-next-line no-console
  console.warn(`[useDateRangeFilter] ${reason}`, detail);
}

export interface UseDateRangeFilterOptions {
  /** Preset por defecto si la URL no trae uno válido. */
  defaultPreset?: string | null;
  /** Prefijo de los params (para coexistir con otros filtros). */
  paramPrefix?: string;
}

/**
 * Hook compartido para leer/escribir el filtro de fechas en la URL.
 *
 * Reglas de fallback documentadas (todas registran `console.warn` trackeable):
 *
 * 1. `datePreset` desconocido → se usa `defaultPreset` (o `null`).
 * 2. `customFrom` ISO inválido → preset custom degrada a `defaultPreset`.
 * 3. `customTo` ausente/ inválido pero `customFrom` válido → se trata como
 *    día completo (`customTo = customFrom`), nunca devuelve rango vacío.
 * 4. `customFrom > customTo` → se invierten dentro de `normalizeDayRange`
 *    para garantizar `from <= to`.
 * 5. Preset distinto de `custom` con `customFrom`/`customTo` presentes →
 *    se ignoran y se aplica el rango del preset.
 *
 * El componente automáticamente reacciona a cambios de URL (back/forward,
 * deep links, recargas) porque `useSearchParams` re-renderiza cuando la
 * `Location` de React Router cambia — incluido el evento `popstate` que
 * BrowserRouter ya escucha internamente.
 */
export function useDateRangeFilter(options: UseDateRangeFilterOptions = {}) {
  const { defaultPreset = null, paramPrefix = "" } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const presetKey = `${paramPrefix}datePreset`;
  const fromKey = `${paramPrefix}customFrom`;
  const toKey = `${paramPrefix}customTo`;

  const rawPreset = searchParams.get(presetKey);
  const rawFrom = searchParams.get(fromKey);
  const rawTo = searchParams.get(toKey);

  // 1. Preset
  let preset: string | null;
  let fallbackReason: string | null = null;
  if (rawPreset && !VALID_PRESETS.has(rawPreset)) {
    warn("preset desconocido, usando fallback", { rawPreset, defaultPreset });
    fallbackReason = `Preset "${rawPreset}" no válido. Se aplicó "${defaultPreset ?? "ninguno"}".`;
    preset = defaultPreset;
  } else {
    preset = rawPreset || defaultPreset;
  }

  // 2-3. Fechas custom
  const parsedFrom = parseISODate(rawFrom);
  const parsedTo = parseISODate(rawTo);
  if (rawFrom && !parsedFrom) {
    warn("customFrom ISO inválido, ignorado", { rawFrom });
    fallbackReason = fallbackReason ?? `Fecha "desde" inválida en URL. Se aplicó "${defaultPreset ?? "ninguno"}".`;
  }
  if (rawTo && !parsedTo) {
    warn("customTo ISO inválido, tratando como día completo", { rawTo });
    fallbackReason = fallbackReason ?? `Fecha "hasta" inválida en URL. Se trató como día completo.`;
  }

  // Si está marcado custom/quarter-pick pero falta customFrom → degradar
  if ((preset === "custom" || preset === "quarter-pick") && !parsedFrom) {
    warn("preset=custom sin customFrom válido, fallback a defaultPreset", { defaultPreset });
    fallbackReason = fallbackReason ?? `Rango personalizado sin fechas. Se aplicó "${defaultPreset ?? "ninguno"}".`;
    preset = defaultPreset && defaultPreset !== "custom" ? defaultPreset : null;
  }

  const customValid = (preset === "custom" || preset === "quarter-pick") && parsedFrom != null;
  const customFrom = customValid ? parsedFrom : null;
  // 3. customTo ausente → día completo
  const customTo = customValid ? (parsedTo ?? parsedFrom) : null;

  // 4. Fechas invertidas → normalizeDayRange las gira; registramos un
  //    fallbackReason para que el toast lo anuncie al usuario.
  if (customValid && customTo && customFrom && customFrom.getTime() > customTo.getTime()) {
    warn("customFrom > customTo, se invertirán automáticamente", { rawFrom, rawTo });
    fallbackReason = fallbackReason ?? `Rango personalizado invertido en URL. Se reordenaron las fechas.`;
  }

  const activeRange = useMemo(() => {
    if (preset === "custom" || preset === "quarter-pick") {
      // normalizeDayRange se encarga de la regla 4 (invertir si from > to)
      return customFrom ? normalizeDayRange(customFrom, customTo ?? customFrom) : null;
    }
    return preset ? getDateRange(preset) : null;
  }, [preset, customFrom, customTo]);

  const setFilter = useCallback(
    (next: { preset: string | null; customFrom: Date | null; customTo: Date | null }) => {
      const nextParams = new URLSearchParams(searchParams);
      const validPreset =
        next.preset && VALID_PRESETS.has(next.preset) ? next.preset : null;
      if (next.preset && !validPreset) {
        warn("setFilter: preset inválido, ignorado", { preset: next.preset });
      }

      if (validPreset) nextParams.set(presetKey, validPreset);
      else nextParams.delete(presetKey);

      if ((validPreset === "custom" || validPreset === "quarter-pick") && next.customFrom) {
        const normalized = normalizeDayRange(
          next.customFrom,
          next.customTo ?? next.customFrom
        );
        nextParams.set(fromKey, normalized.from.toISOString());
        nextParams.set(toKey, normalized.to.toISOString());
      } else {
        nextParams.delete(fromKey);
        nextParams.delete(toKey);
      }
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams, presetKey, fromKey, toKey]
  );

  return { preset, customFrom, customTo, activeRange, setFilter, fallbackReason };
}
